import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const rows: { label: string; before: ReactNode; after: ReactNode }[] = [
  { label: "Overhead (45-50% of revenue)", before: <>~£1.1-1.25M</>, after: <>Reduced</> },
  { label: "Paralegal/admin per case", before: <>5-10 hours</>, after: <>Minutes</> },
  { label: "Case management software", before: <>~£10K/year (Clio)</>, after: <>Replaced by COSX</> },
  { label: "Marketing spend", before: <>£50-125K/year</>, after: <>AI-powered, included</> },
  { label: "COSX overhead value replaced", before: <>—</>, after: <><B>£150-250K/year</B></> },
];

export function V3HowWeEarnSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[28px] font-light tracking-tight mb-2 font-[family-name:var(--font-heading)]">
        Fee-for-service per case. We grow when the firm grows.
      </h2>
      <p className="text-[12px] text-[var(--color-fg-secondary)] font-light leading-relaxed mb-4">
        COSX&apos;s AI replaces a significant portion of the firm&apos;s overhead. Per-case pricing benchmarked against Raymond&apos;s overhead profile. <strong className="font-medium text-[var(--color-fg)]">Replicates across firms with similar revenue structures.</strong>
      </p>

      {/* Before / After table */}
      <div className="rounded-sm mb-4">
        <div className="grid grid-cols-[2fr_1fr_1fr] text-[10px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-wider">
          <div className="px-3 py-2 text-[var(--color-fg-tertiary)]">
            Raymond Legal · £2.5M/year
          </div>
          <div className="px-3 py-2 text-center text-[var(--color-fg-tertiary)]">
            Before COSX
          </div>
          <div className="px-3 py-2 text-center bg-[var(--color-accent)] text-white rounded-t-sm">
            With COSX
          </div>
        </div>
        {rows.map((r, i) => (
          <div
            key={r.label}
            className={`grid grid-cols-[2fr_1fr_1fr] text-[11px] ${i < rows.length - 1 ? "border-b border-[var(--color-border)]" : ""}`}
          >
            <div className="px-3 py-2 text-[var(--color-fg-secondary)]">{r.label}</div>
            <div className="px-3 py-2 text-center text-[var(--color-fg-tertiary)]">{r.before}</div>
            <div
              className={`px-3 py-2 text-center bg-[var(--color-accent)]/5 border-x border-[var(--color-accent)]/20 text-[var(--color-fg)] ${
                i === rows.length - 1 ? "border-b border-[var(--color-accent)]/20 rounded-b-sm" : ""
              }`}
            >
              {r.after}
            </div>
          </div>
        ))}
      </div>

      {/* Two-phase revenue model — subordinate, lighter than the table */}
      <div className="grid grid-cols-2 gap-4 mt-1">
        <div className="border-l-2 border-[var(--color-accent)]/40 pl-3 py-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-accent)]">
              Today
            </span>
            <span className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">
              Platform back-office · all markets
            </span>
          </div>
          <p className="text-[10px] text-[var(--color-fg-secondary)] leading-relaxed">
            Fee-for-service per case via MSA. Same model across Raymond (UK), Ratner (Singapore), RidgeField Capital (Singapore).
          </p>
        </div>
        <div className="border-l-2 border-[var(--color-fg-tertiary)]/30 pl-3 py-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)]">
              Future
            </span>
            <span className="text-[10px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] uppercase tracking-wider">
              ABS equity · UK
            </span>
          </div>
          <p className="text-[10px] text-[var(--color-fg-secondary)] leading-relaxed">
            COSX takes equity in the firm. <B>Profit share through ownership</B> on top of per-case fees. ABS becomes a platform entity for roll-up.
          </p>
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light leading-relaxed">
          More cases → better agents → lower cost per case → new market segments. <strong className="font-medium text-[var(--color-fg)]">This is how the service compounds.</strong>
        </p>
      </div>
    </div>
  );
}
